import React, {createContext, RefObject, useCallback, useEffect, useRef, useState} from 'react';

import {getFromLocal, saveToLocal} from '../../util/storageUtil';

const MENU_COLLAPSED_KEY = 'menuCollapsed';

interface LayoutContextShape {
    sidebarRef: RefObject<HTMLDivElement | null>;
    menuCollapsed: boolean;
    toggleMenu: () => void;
    setMenuCollapsed: (collapsed: boolean) => void;
}

export const LayoutContext = createContext<LayoutContextShape>({
    sidebarRef: {current: null},
    menuCollapsed: false,
    toggleMenu: () => {},
    setMenuCollapsed: () => {}
});

export function LayoutProvider({children}: {children: React.ReactNode}) {
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [menuCollapsed, _setMenuCollapsed] = useState<boolean>(() => getFromLocal(MENU_COLLAPSED_KEY) === 'true');
    const [forceMenuOpen, setForceMenuOpen] = useState(window.matchMedia('(min-width: 1280px)').matches);

    const setMenuCollapsed = useCallback((collapsed: boolean) => {
        saveToLocal(MENU_COLLAPSED_KEY, String(collapsed));
        _setMenuCollapsed(collapsed);
    }, []);

    const toggleMenu = useCallback(() => {
        _setMenuCollapsed(collapsed => {
            const next = !collapsed;
            saveToLocal(MENU_COLLAPSED_KEY, String(next));
            return next;
        });
    }, []);

    useEffect(() => {
        const matcher = window.matchMedia('(min-width: 1280px)');

        function handler(evt: MediaQueryListEvent) {
            setForceMenuOpen(evt.matches);
        }

        matcher.addEventListener('change', handler, {passive: true});
        return () => matcher.removeEventListener('change', handler);
    }, []);

    return (
        <LayoutContext.Provider
            value={{sidebarRef, menuCollapsed: menuCollapsed && !forceMenuOpen, toggleMenu, setMenuCollapsed}}
        >
            {children}
        </LayoutContext.Provider>
    );
}
